import * as df from 'date-fns';
import { remove } from 'lodash-es';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksTextButton from '../../../../components/books/common/buttons/BooksTextButton';
import BooksSelectedDateTime from '../../../../components/books/common/inputs/BooksSelectedDateTime';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import * as workflow from '../../../../redux/actions/merchants/resources/books/workflow';
import { default as Header } from './InputBookingEventDateTimeCommonHeader';
import InputSchoolCalendarDateTimePicker from './InputSchoolCalendarDateTimePicker';

import css from './InputBookingEventDateTimeCalendar.module.scss';

interface Props extends InjectedIntlProps {
  loggedIn: boolean;
  initialEventTimeSlot?: IInputs.EventTimeSlot;
  settings: IBooks.SettingEvent;
  postable: boolean;
  onSetDateTimePicker: (query: { start: Date; end: Date }) => Promise<void>;
  onAddToWaitingList: (timeSlot: IBooks.EventTimeSlot) => Promise<void>;
  onSubmit: (timeSlots: IInputs.EventTimeSlot) => void;
  withExpressFlow: boolean;
}

interface State {
  selecting: boolean;
  baseDate: Date;
  attendees?: number;
  selectedTimeSlots: IBooks.EventTimeSlot[];
}

class InputBookingEventDateTimeCalendar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      initialEventTimeSlot,
      settings: { selectedTimeSlot },
    } = this.props;
    this.state = {
      selecting: false,
      baseDate: initialEventTimeSlot
        ? df.parse(initialEventTimeSlot.timeSlots[0].date)
        : selectedTimeSlot
        ? df.parse(selectedTimeSlot.date)
        : new Date(),
      attendees: initialEventTimeSlot
        ? initialEventTimeSlot.attendees
        : selectedTimeSlot
        ? 1
        : undefined,
      selectedTimeSlots: initialEventTimeSlot
        ? initialEventTimeSlot.timeSlots
        : selectedTimeSlot
        ? [selectedTimeSlot]
        : [],
    };
    this.handleChangeAttendees = this.handleChangeAttendees.bind(this);
    this.handleStartSelecting = this.handleStartSelecting.bind(this);

    this.handleChangeTimeSlots = this.handleChangeTimeSlots.bind(this);
    this.handleRemoveTimeSlot = this.handleRemoveTimeSlot.bind(this);
    this.handleChangeBaseDate = this.handleChangeBaseDate.bind(this);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public handleChangeAttendees(attendees: number) {
    this.setState({
      ...this.state,
      attendees,
      selectedTimeSlots: [],
    });
  }

  private handleStartSelecting() {
    workflow.tick({
      onForwardCallback: () => {
        this.setState({ ...this.state, selecting: true });
      },
      onBackCallback: () => {
        this.setState({ ...this.state, selecting: false });
      },
    });
    this.setState({ ...this.state, selecting: true });
  }

  private handleChangeTimeSlots(timeSlots: IBooks.EventTimeSlot[]) {
    workflow.back();
    this.setState({
      ...this.state,
      selecting: false,
      selectedTimeSlots: timeSlots,
    });
  }

  private handleRemoveTimeSlot(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const {
      currentTarget: { value },
    } = e;

    const { selectedTimeSlots } = this.state;
    remove(selectedTimeSlots, t => t.id === parseInt(value, 10));
    this.setState({ ...this.state, selectedTimeSlots });
  }

  private handleChangeBaseDate(baseDate: Date) {
    const { onSetDateTimePicker } = this.props;

    this.setState({ ...this.state, baseDate });
    onSetDateTimePicker({
      start: df.startOfMonth(baseDate),
      end: df.endOfMonth(baseDate),
    });
  }

  private handleSubmit() {
    const { selectedTimeSlots, attendees } = this.state;
    this.props.onSubmit({
      timeSlots: selectedTimeSlots,
      attendees: attendees || 1,
    });
  }

  public render() {
    const {
      loggedIn,
      settings,
      postable,
      onAddToWaitingList,
      withExpressFlow,
      intl,
    } = this.props;
    const { selecting, baseDate, selectedTimeSlots, attendees } = this.state;
    return !selecting ? (
      <div className={css.InputBookingEventDateTimeCalendar}>
        <Header
          settings={settings}
          attendees={attendees}
          onChangeAttendees={this.handleChangeAttendees}
          withExpressFlow={withExpressFlow}
        />
        <BooksLabeledContainer
          label={
            settings.multipleSelectSlots
              ? intl.formatMessage({
                  id: 'books.formsLabel.multipleBookingDates',
                })
              : intl.formatMessage({
                  id: 'books.booksMenu.bookDate',
                })
          }
          paddingTop={true}
        >
          {selectedTimeSlots.map(timeSlot => (
            <BooksSelectedDateTime
              key={timeSlot.id}
              date={timeSlot.date}
              startTime={timeSlot.startTime}
              endTime={timeSlot.endTime}
              onTimeSlotClick={
                settings.multipleSelectSlots ? null : this.handleStartSelecting
              }
              onDeleteClick={
                settings.multipleSelectSlots ? this.handleRemoveTimeSlot : null
              }
              value={timeSlot.id}
              withBorder={settings.multipleSelectSlots}
            />
          ))}
          {!settings.multipleAttendees.allowed ||
          (settings.multipleAttendees.allowed && !!attendees) ? (
            selectedTimeSlots.length === 0 || settings.multipleSelectSlots ? (
              <BooksTextButton onClick={this.handleStartSelecting}>
                {intl.formatMessage({
                  id: 'books.forms.addDateAndTime',
                })}
              </BooksTextButton>
            ) : null
          ) : (
            <span
              className={css.InputBookingEventDateTimeCalendar__PlaceHolder}
            >
              {intl.formatMessage({
                id: 'books.forms.pleaseSelectAttendeesFirst',
              })}
            </span>
          )}
        </BooksLabeledContainer>
        <BooksSubmitButton
          disabled={selectedTimeSlots.length === 0 || !postable}
          postable={postable}
          onClick={this.handleSubmit}
        >
          {intl.formatMessage({ id: 'books.submitLabel.next' })}
        </BooksSubmitButton>
      </div>
    ) : (
      <InputSchoolCalendarDateTimePicker
        postable={postable}
        selection={settings.multipleSelectSlots ? 'multiple' : 'single'}
        baseDate={baseDate}
        timeSlots={settings.timeSlots}
        showVacancy={settings.showVacancy}
        onChange={this.handleChangeTimeSlots}
        onChangeBaseDate={this.handleChangeBaseDate}
        selectedTimeSlots={selectedTimeSlots}
        attendees={attendees}
        waitingList={
          settings.waitingListProvided
            ? {
                list: settings.waitingList,
                onAddToWaitingList,
                loggedIn,
                errors: settings!.addWaitingListErrors,
              }
            : undefined
        }
      />
    );
  }
}

export default injectIntl(InputBookingEventDateTimeCalendar);
